import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Input } from "../components/ui/Input";
import { authService, type AccountProfile } from "../services/authService";
import { sellerService, type CreateListingFormInput } from "../services/sellerService";
import type { Listing } from "../types/domain";

const emptyForm: CreateListingFormInput = {
  ndc: "",
  productName: "",
  strength: "",
  dosageForm: "",
  packageSize: "",
  manufacturer: "",
  lotNumber: "",
  expirationDate: "",
  quantityAvailable: "1",
  unitPrice: "",
  holdWindowMinutes: "1440",
  shippingOriginState: "WA",
};

export function CreateListingPage({ initialProfile }: { initialProfile?: AccountProfile } = {}) {
  const [profile, setProfile] = useState<AccountProfile | null>(initialProfile ?? null);
  const [form, setForm] = useState<CreateListingFormInput>(emptyForm);
  const [createdListing, setCreatedListing] = useState<Listing | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isActivating, setIsActivating] = useState(false);

  useEffect(() => {
    if (initialProfile) return;
    let cancelled = false;
    authService.loadProfile()
      .then(nextProfile => { if (!cancelled) setProfile(nextProfile); })
      .catch((err: Error) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, []);

  const update = (field: keyof CreateListingFormInput) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    setForm(current => ({ ...current, [field]: value }));
  };

  return (
    <div className="space-y-5">
      <Card className="p-8">
        <p className="text-xs font-black uppercase tracking-[.28em] text-slate-500">Seller inventory</p>
        <div className="mt-3 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-4xl font-black">Add listing</h1>
            <p className="mt-3 max-w-3xl text-slate-600">Create a draft pharmaceutical inventory listing for your verified pharmacy. Activate it after review when ready to appear in the marketplace.</p>
          </div>
          <Button href="/seller/inventory" variant="secondary">Back to inventory</Button>
        </div>
      </Card>

      <Card className="p-6">
        <form className="grid gap-5" onSubmit={async event => {
          event.preventDefault();
          if (!profile) {
            setError("Sign in as a seller pharmacy before creating a listing.");
            return;
          }
          setIsSubmitting(true);
          setError(null);
          setStatusMessage(null);
          try {
            const submission = Object.fromEntries(new FormData(event.currentTarget).entries()) as unknown as CreateListingFormInput;
            const listing = await sellerService.createLiveListing(submission, profile);
            setCreatedListing(listing);
            setStatusMessage(`Draft listing created: ${listing.name}`);
          } catch (err) {
            setError(err instanceof Error ? err.message : "Unable to create listing");
          } finally {
            setIsSubmitting(false);
          }
        }}>
          <div className="grid gap-4 md:grid-cols-2">
            <Input label="NDC" name="ndc" placeholder="00074-4339-02" value={form.ndc} onChange={update("ndc")} required />
            <Input label="Product name" name="productName" placeholder="Humira" value={form.productName} onChange={update("productName")} required />
            <Input label="Strength" name="strength" placeholder="40mg/0.4mL" value={form.strength} onChange={update("strength")} required />
            <Input label="Dosage form" name="dosageForm" placeholder="Pen" value={form.dosageForm} onChange={update("dosageForm")} required />
            <Input label="Package size" name="packageSize" placeholder="2 pens" value={form.packageSize} onChange={update("packageSize")} required />
            <Input label="Manufacturer" name="manufacturer" placeholder="AbbVie" value={form.manufacturer} onChange={update("manufacturer")} required />
            <Input label="Lot number" name="lotNumber" placeholder="HU-44Q" value={form.lotNumber} onChange={update("lotNumber")} required />
            <Input label="Expiration date" name="expirationDate" type="date" value={form.expirationDate} onChange={update("expirationDate")} required />
            <Input label="Quantity available" name="quantityAvailable" type="number" min="1" value={form.quantityAvailable} onChange={update("quantityAvailable")} required />
            <Input label="Unit price" name="unitPrice" type="number" min="0.01" step="0.01" value={form.unitPrice} onChange={update("unitPrice")} required />
            <Input label="Hold window minutes" name="holdWindowMinutes" type="number" min="1" value={form.holdWindowMinutes} onChange={update("holdWindowMinutes")} required />
            <Input label="Shipping origin state" name="shippingOriginState" maxLength={2} value={form.shippingOriginState} onChange={update("shippingOriginState")} required />
          </div>

          {error ? <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700">{error}</p> : null}
          {statusMessage ? <p className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm font-semibold text-emerald-800">{statusMessage}</p> : null}

          <div className="flex flex-wrap gap-3">
            <button className="rounded-2xl bg-slate-950 px-5 py-3 font-black text-white disabled:opacity-60" type="submit" disabled={isSubmitting}>{isSubmitting ? "Creating…" : "Create draft listing"}</button>
            {createdListing ? (
              <button className="rounded-2xl border border-slate-200 bg-white px-5 py-3 font-black text-slate-950 disabled:opacity-60" type="button" disabled={isActivating} onClick={async () => {
                setIsActivating(true);
                setError(null);
                try {
                  await sellerService.activateLiveListing(createdListing.id);
                  setCreatedListing({ ...createdListing, status: "active" });
                  setStatusMessage(`Listing activated: ${createdListing.name}`);
                } catch (err) {
                  setError(err instanceof Error ? err.message : "Unable to activate listing");
                } finally {
                  setIsActivating(false);
                }
              }}>{isActivating ? "Activating…" : "Activate listing"}</button>
            ) : null}
          </div>
        </form>
      </Card>

      {createdListing ? (
        <Card className="p-6">
          <h2 className="text-2xl font-black">Created listing</h2>
          <p className="mt-2 text-slate-600">{createdListing.name} {createdListing.dosage} · {createdListing.quantity} units · ${createdListing.unitPrice.toLocaleString()} / unit</p>
          <Link className="mt-4 inline-block rounded-xl border border-slate-200 px-4 py-2 font-black text-slate-950" to={`/marketplace/listings/${createdListing.id}`}>View listing</Link>
        </Card>
      ) : null}
    </div>
  );
}
